import { useState, useMemo } from 'react';
import { useTasks } from '../hooks/useTasks';
import { useDeals } from '../hooks/useDeals';
import { useContacts } from '../hooks/useContacts';
import { useToast } from '../hooks/useToast';
import TaskModal from '../components/tasks/TaskModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ToastContainer } from '../components/common/Toast';

// Define statuses for Kanban columns
const TASK_STATUSES = ['todo', 'in_progress', 'completed'];
const STATUS_LABELS = {
  todo: 'To Do',
  in_progress: 'In Progress',
  completed: 'Completed'
};

const Tasks = () => {
  const { tasks, loading, addTask, updateTask, deleteTask } = useTasks();
  const { deals } = useDeals();
  const { contacts } = useContacts();
  const toast = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Group tasks by status
  const tasksByStatus = useMemo(() => {
    const filtered = tasks.filter(task =>
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return TASK_STATUSES.reduce((acc, status) => {
      acc[status] = filtered
        .filter(task => task.status === status)
        .sort((a, b) => {
          // Sort by priority first (urgent > high > medium > low)
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
          if (priorityDiff !== 0) return priorityDiff;
          // Then by due date
          return new Date(a.dueDate) - new Date(b.dueDate);
        });
      return acc;
    }, {});
  }, [tasks, searchQuery]);

  const getDealTitle = (dealId) => {
    const deal = deals.find(d => d.id === dealId);
    return deal ? deal.title : null;
  };

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact ? contact.name : null;
  };

  const handleAddTask = () => {
    setEditingTask(null);
    setIsModalOpen(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setIsModalOpen(true);
  };

  const handleCreateTask = async (formData) => {
    setSubmitting(true);
    try {
      await addTask(formData);
      setIsModalOpen(false);
      toast.success('Task created successfully');
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTask = async (formData) => {
    if (!editingTask) return;

    setSubmitting(true);
    try {
      await updateTask(editingTask.id, formData);
      setIsModalOpen(false);
      setEditingTask(null);
      toast.success('Task updated successfully');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
      toast.success('Task status updated');
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await deleteTask(taskId);
      toast.success('Task deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: 'bg-slate-400',
      in_progress: 'bg-blue-500',
      completed: 'bg-green-500'
    };
    return colors[status] || 'bg-slate-400';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
      case 'low':
        return 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
      default:
        return 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-400 border-slate-200 dark:border-slate-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const taskDate = new Date(date);
    taskDate.setHours(0, 0, 0, 0);

    if (taskDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (taskDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else if (taskDate < today) {
      return `Overdue`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const isOverdue = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" message="Loading tasks..." />
      </div>
    );
  }

  return (
    <main className="flex-1 flex flex-col h-full relative overflow-hidden bg-background-light dark:bg-background-dark">
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />

      <TaskModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTask(null);
        }}
        task={editingTask}
        onSubmit={editingTask ? handleUpdateTask : handleCreateTask}
        loading={submitting}
      />

      {/* Top Navigation / Header */}
      <header className="shrink-0 border-b border-border-dark/50 bg-background-light dark:bg-background-dark/95 backdrop-blur z-10">
        <div className="px-6 py-5 flex flex-col gap-6">
          {/* Page Title & Primary Action */}
          <div className="flex flex-wrap justify-between items-end gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-slate-900 dark:text-white text-3xl font-bold tracking-tight">Tasks</h2>
              <p className="text-slate-500 dark:text-text-secondary text-sm">
                Manage your upcoming tasks and to-dos ({tasks.length} total)
              </p>
            </div>
            <button
              onClick={handleAddTask}
              className="flex items-center justify-center gap-2 bg-primary hover:bg-blue-600 text-white px-5 py-2.5 rounded-lg shadow-lg shadow-primary/20 transition-all active:scale-95"
            >
              <span className="material-symbols-outlined text-[20px]">add</span>
              <span className="text-sm font-bold tracking-wide">New Task</span>
            </button>
          </div>
          {/* Search */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative w-full max-w-sm group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="material-symbols-outlined text-slate-400 dark:text-text-secondary group-focus-within:text-primary transition-colors text-[20px]">
                  search
                </span>
              </div>
              <input
                className="block w-full pl-10 pr-3 py-2.5 border border-slate-200 dark:border-border-dark rounded-lg bg-white dark:bg-[#233648] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-text-secondary focus:ring-2 focus:ring-primary transition-all text-sm"
                placeholder="Search tasks..."
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Kanban Board Area */}
      {tasks.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-12">
            <span className="material-symbols-outlined text-slate-300 dark:text-slate-700 text-[64px] mb-4 block">task_alt</span>
            <h3 className="text-slate-900 dark:text-white text-lg font-bold mb-2">No tasks yet</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
              Create your first task to get started
            </p>
            <button
              onClick={handleAddTask}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">add</span>
              Create Task
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto p-6 bg-background-light dark:bg-background-dark">
          <div className="flex h-full gap-6 min-w-[900px]">
            {TASK_STATUSES.map(status => (
              <div key={status} className="flex flex-col w-[320px] shrink-0">
                {/* Column Header */}
                <div className="flex items-center justify-between mb-4 px-1">
                  <div className="flex items-center gap-2">
                    <div className={`size-2 rounded-full ${getStatusColor(status)}`}></div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                      {STATUS_LABELS[status]}
                    </h3>
                    <span className="ml-1 px-2 py-0.5 rounded-md bg-slate-100 dark:bg-[#233648] text-xs text-slate-600 dark:text-text-secondary font-medium">
                      {tasksByStatus[status].length}
                    </span>
                  </div>
                </div>

                {/* Cards */}
                <div className="flex flex-col gap-3 h-full overflow-y-auto pb-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                  {tasksByStatus[status].length === 0 ? (
                    <div className="text-center py-8 text-slate-400 dark:text-text-secondary text-sm">
                      No tasks
                    </div>
                  ) : (
                    tasksByStatus[status].map(task => {
                      const dealTitle = task.dealId ? getDealTitle(task.dealId) : null;
                      const contactName = task.contactId ? getContactName(task.contactId) : null;
                      const overdue = task.status !== 'completed' && isOverdue(task.dueDate);

                      return (
                        <div
                          key={task.id}
                          onClick={() => handleEditTask(task)}
                          className="bg-white dark:bg-card-dark rounded-xl p-4 border border-slate-200 dark:border-border-dark/30 hover:border-primary/50 dark:hover:border-primary/60 cursor-pointer group shadow-sm hover:shadow-md dark:shadow-none transition-all"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide border ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTask(task.id);
                              }}
                              className="text-slate-400 dark:text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500 dark:hover:text-red-400 p-1"
                            >
                              <span className="material-symbols-outlined text-[18px]">delete</span>
                            </button>
                          </div>

                          <h4 className="text-slate-900 dark:text-white font-semibold mb-1 group-hover:text-primary transition-colors">
                            {task.title}
                          </h4>

                          {task.description && (
                            <p className="text-slate-600 dark:text-slate-300 text-xs mb-3 line-clamp-2">
                              {task.description}
                            </p>
                          )}

                          {/* Associated Deal/Contact */}
                          {(dealTitle || contactName) && (
                            <div className="flex flex-col gap-1 mb-3">
                              {dealTitle && (
                                <div className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
                                  <span className="material-symbols-outlined text-[14px]">view_kanban</span>
                                  <span className="truncate">{dealTitle}</span>
                                </div>
                              )}
                              {contactName && (
                                <div className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
                                  <span className="material-symbols-outlined text-[14px]">person</span>
                                  <span className="truncate">{contactName}</span>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-border-dark/50">
                            <div className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-500 dark:text-slate-400'}`}>
                              <span className="material-symbols-outlined text-[16px]">
                                {overdue ? 'warning' : 'calendar_today'}
                              </span>
                              {formatDate(task.dueDate)}
                            </div>

                            {/* Status selector */}
                            <select
                              value={task.status}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStatusChange(task.id, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs bg-transparent border border-slate-300 dark:border-border-dark rounded px-2 py-1 cursor-pointer hover:border-primary dark:hover:border-primary text-slate-700 dark:text-slate-300 transition-colors"
                            >
                              {TASK_STATUSES.map(s => (
                                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
};

export default Tasks;
